import React, { useState, useEffect } from "react";
import axios from "axios";
import "../community.css";
import "../ai.css";
import { Link, useNavigate } from "react-router-dom";
import {
  updateDoc,
  doc,
  increment,
  addDoc,
  collection,
  serverTimestamp
} from "firebase/firestore";
import { auth, db } from "../../firebase";

/* ==========================================================
   Cover Letter Builder
========================================================== */

function CoverLetterGenerator() {

  const [details, setDetails] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [loading, setLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const MAX_CHARS = 8000;

  const [pdfUrl, setPdfUrl] = useState("");

  const [selectedTemplate, setSelectedTemplate] = useState("classic");
  const [selectedVariant, setSelectedVariant] = useState("default");


useEffect(() => {

  const pendingLetter =
    sessionStorage.getItem("pendingCoverLetter");

  const pendingPdf =
    sessionStorage.getItem("pendingCoverLetterPdf");

  if (pendingLetter) {
    setCoverLetter(pendingLetter);
    sessionStorage.removeItem("pendingCoverLetter");
  }

  if (pendingPdf) {
    setPdfUrl(pendingPdf);
    sessionStorage.removeItem("pendingCoverLetterPdf");
  }

}, []);

  const getTemplateAccentColor = () => {
    if (selectedVariant === "modern-blue") return "#4682B4";
    if (selectedVariant === "modern-purple") return "#805AD5";
    if (selectedVariant === "modern-green") return "#228B22";

    if (selectedVariant === "elite-gray") return "#787878";
    if (selectedVariant === "elite-black") return "#000000";
    if (selectedVariant === "elite-emerald") return "#008C64";

    if (selectedVariant === "executive-gold") return "#B4963C";
    if (selectedVariant === "executive-navy") return "#193264";
    if (selectedVariant === "executive-charcoal") return "#505050";

    if (selectedTemplate === "modern") return "#4682B4";
    if (selectedTemplate === "elite") return "#787878";
    if (selectedTemplate === "executive") return "#B4963C";

    return "#999";
  };

  /* ==========================================================
     Generate Cover Letter
  ========================================================== */

  const generateCoverLetter = async () => {

  if (!details.trim()) {
    alert("Please describe yourself and paste the job description.");
    return;
  }

  if (details.length > MAX_CHARS) {
    alert("Your request is too long. Please shorten it.");
    return;
  }

  setLoading(true);
  setCoverLetter("");

  try {

    const token = await auth.currentUser.getIdToken();

    const res = await axios.post(
      `${apiBase}/api/generate-cover-letter`,
      {
        details,
        template: selectedTemplate,
        variant: selectedVariant
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const generatedLetter = res.data.cover_letter || "";
    const generatedPdfUrl = res.data.pdf_url || "";

    setCoverLetter(generatedLetter);
    setPdfUrl(generatedPdfUrl);

    // Persist after refresh
    sessionStorage.setItem(
      "pendingCoverLetter",
      generatedLetter
    );

    sessionStorage.setItem(
      "pendingCoverLetterPdf",
      generatedPdfUrl
    );

    const user = auth.currentUser;

    // Save PDF Library
    if (user && generatedPdfUrl) {

      await addDoc(
        collection(
          db,
          "users",
          user.uid,
          "coverLetters"
        ),
        {
          title: "Professional Cover Letter",
          pdfUrl: generatedPdfUrl,
          createdAt: serverTimestamp(),
        }
      );

      // Career Progress
      await updateDoc(
        doc(db, "users", user.uid),
        {
          "progress.coverLetters": increment(1)
        }
      );

    }

  } catch (err) {

    console.error("Cover Letter Error:", err);

    setCoverLetter(
      "Error generating your cover letter. Please try again."
    );

  } finally {

    setLoading(false);

  }

};

  // =====================================
  // OPEN PDF
  // =====================================

  const openPDF = (url) => {

    if (!url) return;

    if (window.innerWidth < 768) {

      navigate("/pdf-viewer", {
        state: {
          pdfUrl: url
        }
      });

    } else {

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );

    }

  };

  // =====================================
  // DOWNLOAD PDF
  // =====================================

  const handleDownloadPDF = () => {

    openPDF(pdfUrl);

  };

  /* ==========================================================
     Copy to Clipboard
  ========================================================== */

  const copyLetter = async () => {

    try {

      await navigator.clipboard.writeText(coverLetter);

      alert("Cover Letter copied!");

    } catch {

      alert("Unable to copy.");

    }

  };

  /* ==========================================================
     UI
  ========================================================== */

  return (

    <div className="ai-tool-wrapper">

      <Link to="/community">
        <button className="back-btn">
          ← Back to Community
        </button>
      </Link>

      <h2 className="ai-tool-title">
        📄 Cover Letter Generator
      </h2>

      <p
        style={{
          textAlign: "center",
          color: "#777",
          marginBottom: "18px"
        }}
      >
        Build a Professional Cover Letter in Seconds.
      </p>

      <textarea
        className="ai-input"
        placeholder={`Paste the job description and tell EmployPilot about your experience, skills, and career goals.

The more context you provide, the better your personalized cover letter will be.

Example:

Position:
Software Engineer

Company:
Google

My Experience:
• 3 years building React applications
• Python & FastAPI development
• Firebase & Stripe integrations

Job Description:
(Paste here)

Additional Notes:
I enjoy solving complex problems and working with AI technologies.`}
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        maxLength={MAX_CHARS}
      />

      <div className="char-counter">
        {details.length} / {MAX_CHARS}
      </div>

      <div style={{ marginTop: "18px", marginBottom: "16px" }}>
        <label
          style={{
            display: "block",
            fontWeight: "700",
            marginBottom: "8px",
            color: "#333"
          }}
        >
          Choose Cover Letter Style
        </label>

        <select
          className="input-field"
          value={selectedTemplate}
          onChange={(e) => {
            setSelectedTemplate(e.target.value);
            setSelectedVariant("default");
          }}
        >
          <option value="classic">Classic</option>
          <option value="modern">Modern</option>
          <option value="elite">Elite</option>
          <option value="executive">Executive</option>
        </select>

        {selectedTemplate !== "classic" && (
          <select
            className="input-field"
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
            style={{ marginTop: "10px" }}
          >
            <option value="default">Default Style</option>

            {selectedTemplate === "modern" && (
              <>
                <option value="modern-blue">Modern Blue</option>
                <option value="modern-purple">Modern Purple</option>
                <option value="modern-green">Modern Green</option>
              </>
            )}

            {selectedTemplate === "elite" && (
              <>
                <option value="elite-gray">Elite Gray</option>
                <option value="elite-black">Elite Black</option>
                <option value="elite-emerald">Elite Emerald</option>
              </>
            )}

            {selectedTemplate === "executive" && (
              <>
                <option value="executive-gold">Executive Gold</option>
                <option value="executive-navy">Executive Navy</option>
                <option value="executive-charcoal">Executive Charcoal</option>
              </>
            )}
          </select>
        )}

        <div style={{ marginTop: "10px" }}>
          <div
            style={{
              width: "100%",
              height: "6px",
              borderRadius: "4px",
              background: getTemplateAccentColor(),
            }}
          />
        </div>
      </div>

      <button
        className="ai-btn"
        onClick={generateCoverLetter}
        disabled={loading}
      >

        {loading
          ? "Generating..."
          : "Generate Professional Cover Letter"}

      </button>

      <p className="ai-tip">
      💡 <strong>Tip:</strong> Review and personalize your cover letter before
        submitting it to employers.
      </p>

      {coverLetter && (

        <div className="ai-output">

          <h3 className="ai-output-title">

            Your Cover Letter

          </h3>

          <pre className="ai-output-box">

            {coverLetter}

          </pre>

          <p
          style={{
            fontSize: "14px",
            color: "#666",
            marginTop: "12px",
            marginBottom: "10px",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          ✨ Download the styled professional PDF version below.
        </p>

        <button
          className="ai-btn"
          onClick={handleDownloadPDF}
        >
          Download Professional PDF
        </button>

          <button
            className="ai-btn"
            onClick={copyLetter}
            style={{ marginTop: "20px" }}
          >
            Copy Cover Letter
          </button>

        </div>

      )}

    </div>

  );

}

export default CoverLetterGenerator;
